import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  ScanLine,
  ListChecks,
  Wand2,
  FileText,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
} from "lucide-react";

const PAGE_TITLE =
  "How uSeen Works | AI Visibility Analysis for Local Businesses";
const PAGE_DESC =
  "uSeen analyzes your business, compares your digital presence with competitors, finds visibility gaps and turns them into actionable tasks.";

const FAQ_ITEMS = [
  {
    q: "How does uSeen analyse my business?",
    a: "uSeen scans your website, Google Business Profile signals, customer reviews and any third-party mentions, then compares those signals to a defined set of AI visibility criteria.",
  },
  {
    q: "How long does the analysis take?",
    a: "Most scans take about 60-90 seconds. You'll see a live progress overlay while it runs.",
  },
  {
    q: "Will it tell me what to fix?",
    a: "Yes. uSeen converts gaps into prioritised tasks — for example: improve your service descriptions, complete missing GBP fields, build FAQ content on your site, or strengthen review signals.",
  },
  {
    q: "Can I compare myself to competitors?",
    a: "Yes. You can add up to three competitor websites and uSeen will compare your AI visibility signals side-by-side.",
  },
  {
    q: "Do I need technical skills?",
    a: "No. uSeen is designed for owners and operators. Each task is explained in plain English, with clear next steps.",
  },
  {
    q: "Can I re-scan after applying changes?",
    a: "Yes. Re-scans let you measure the impact of your changes. You can re-scan a single task or your whole business.",
  },
];

const STEPS = [
  {
    icon: <Sparkles size={18} />,
    title: "1. Add your business",
    desc: "Tell us your business name, sector, city, website and (optionally) up to three competitors.",
  },
  {
    icon: <ScanLine size={18} />,
    title: "2. Run the AI Visibility scan",
    desc: "uSeen analyses your website, GBP signals, content and review presence — and compares everything against competitors.",
  },
  {
    icon: <TrendingUp size={18} />,
    title: "3. See your score",
    desc: "Get an AI Visibility Score (0-100) plus sub-scores for content, location clarity, services clarity, reviews and more.",
  },
  {
    icon: <ListChecks size={18} />,
    title: "4. Get a prioritised task list",
    desc: "Every gap becomes a task: what to do, why it matters, and what changes when you apply it.",
  },
  {
    icon: <Wand2 size={18} />,
    title: "5. Generate content where it helps",
    desc: "Use the content generator to draft FAQ blocks, service-page copy, GBP descriptions and more — tailored to your business.",
  },
  {
    icon: <FileText size={18} />,
    title: "6. Re-scan and download your report",
    desc: "Re-scan to measure improvements over time and export a PDF report you can share with your team or clients.",
  },
];

/**
 * Public SEO landing page — /en/how-it-works
 * Mirrors /nasil-calisir for English visitors.
 */
export default function HowItWorksEn() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    const ORIGIN = "https://useen.ai";
    const alternates = [
      { hreflang: "en", href: `${ORIGIN}/en/how-it-works` },
      { hreflang: "tr", href: `${ORIGIN}/nasil-calisir` },
      { hreflang: "x-default", href: `${ORIGIN}/en/how-it-works` },
    ];
    document.querySelectorAll('link[rel="alternate"][data-useen-hreflang]').forEach((el) => el.remove());
    const addedLinks = [];
    alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute("data-useen-hreflang", "1");
      document.head.appendChild(link);
      addedLinks.push(link);
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "how-it-works-en");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    const prevHtmlLang = document.documentElement.lang;
    document.documentElement.lang = "en";

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      addedLinks.forEach((l) => l.parentNode?.removeChild(l));
      ld.parentNode?.removeChild(ld);
      document.documentElement.lang = prevHtmlLang;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="how-it-works-en-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Pricing</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Log in</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Start free
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> How uSeen works
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            How uSeen Works
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            uSeen analyses how visible your business is across AI assistants, compares your digital presence
            against competitors and turns the gaps into a prioritised task list — without requiring any technical skill.
          </p>
          <p className="text-slate-400 leading-relaxed mb-10">
            The whole flow takes minutes to set up and produces a clear roadmap you can act on the same day.
          </p>

          <section className="space-y-3 mb-12" data-testid="how-it-works-en-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/5 bg-black/30 p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-300 shrink-0">
                  {s.icon}
                </div>
                <div>
                  <h2 className="font-display text-lg sm:text-xl font-semibold text-white mb-1">{s.title}</h2>
                  <p className="text-slate-300 text-[0.95rem] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </section>

          <Article>
            <Section icon={<CheckCircle2 size={18} />} title="What You Get">
              <BulletList items={[
                "An AI Visibility Score (0-100) with category-level sub-scores",
                "Side-by-side comparison with up to three competitors",
                "A prioritised task list — critical, important and nice-to-have",
                "Per-task explanation: what to change, why, and what improves",
                "Built-in content generator for FAQ blocks, GBP descriptions and more",
                "Re-scan workflow so you can prove the improvement over time",
                "Downloadable PDF report to share with your team or clients",
              ]} />
            </Section>

            <Section icon={<TrendingUp size={18} />} title="Designed for Local Businesses">
              <p>
                uSeen is built specifically for local and service-led businesses — clinics, restaurants, hotels,
                beauty centres, camp sites, real estate, auto services and more. The signals it scores are the ones
                AI assistants actually use to decide who to recommend.
              </p>
            </Section>
          </Article>

          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="how-it-works-en-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                Ready to see your AI visibility?
              </h2>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                Get your first AI Visibility scan for free. No credit card needed.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/kayit" className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2" data-testid="how-it-works-en-cta-primary">
                  Start free <ArrowRight size={14} />
                </Link>
                <Link to="/fiyatlandirma" className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2" data-testid="how-it-works-en-cta-secondary">
                  See plans
                </Link>
              </div>
              <div className="mt-6 text-sm text-slate-400">
                Keep reading: <Link to="/en/what-is-ai-visibility" className="text-cyan-300 hover:text-cyan-200" data-testid="how-it-works-en-internal-aiv">What is AI Visibility?</Link>
              </div>
            </div>
          </div>

          <section className="mt-16" data-testid="how-it-works-en-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Frequently asked questions
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">Common questions</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Got more questions?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="how-it-works-en-contact-link">Get in touch</Link>.
            </div>
          </section>
        </div>
      </main>

      <SiteFooter lang="en" />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
